// Detects Teams host, runs SSO, and exposes a Bearer token for api.ts.
//
// Flow inside a Teams tab:
//   1. teamsjs initialize() — host handshake
//   2. authentication.getAuthToken() — Teams hands us a JWT for our app
//   3. POST /api/auth/teams-sso — backend verifies the JWT, returns a session id
//   4. Store session id in localStorage; api.ts adds Authorization: Bearer
//
// Outside Teams (regular browser tab) this module is a no-op — login proceeds
// through the existing /api/auth/login redirect.

import * as microsoftTeams from "@microsoft/teams-js";

const TEAMS_BEARER_KEY = "juuplan:teams_bearer";

let teamsInitPromise: Promise<boolean> | null = null;

export function getTeamsBearer(): string | null {
  try {
    return localStorage.getItem(TEAMS_BEARER_KEY);
  } catch {
    return null;
  }
}

export function setTeamsBearer(token: string | null): void {
  try {
    if (token) localStorage.setItem(TEAMS_BEARER_KEY, token);
    else localStorage.removeItem(TEAMS_BEARER_KEY);
  } catch {
    // Private mode / quota — silently ignore. The cookie path still works
    // when third-party cookies are allowed.
  }
}

// Heuristic: if the page is iframed and the parent looks like a Teams host,
// or if the URL has been opened with the Teams query flag, try to init.
// teamsjs's own probe (app.initialize) errors fast outside Teams.
export function probablyInTeams(): boolean {
  if (typeof window === "undefined") return false;
  if (window.self === window.top) return false;
  const parentHost = (() => {
    try {
      return window.parent?.location?.host ?? "";
    } catch {
      // Cross-origin parent — we can't read host. That's actually the *normal*
      // Teams case (iframe is sandboxed), so assume it could be Teams.
      return "x-origin";
    }
  })();
  if (parentHost === "x-origin") return true;
  return /teams\.microsoft\.com|teams\.microsoft\.us|teams\.live\.com/.test(
    parentHost
  );
}

async function initTeams(): Promise<boolean> {
  if (!teamsInitPromise) {
    teamsInitPromise = microsoftTeams.app
      .initialize()
      .then(() => {
        // notifySuccess() tells Teams the tab finished loading — without it
        // Teams shows "Etwas ist schief gegangen" after a short timeout on
        // the "Add to chat/team" flow.
        try {
          microsoftTeams.app.notifySuccess();
        } catch {
          // older teams-js versions may not have this — non-fatal
        }
        return true;
      })
      .catch(() => false);
  }
  return teamsInitPromise;
}

// Activity-Feed entries carry our in-app path in the deep-link's subPageId
// (a.k.a. subEntityId in older teams-js). When Teams launches the tab via
// such a link, the path arrives in app.getContext() → we rewrite the URL so
// wouter-preact picks up the right route before mounting.
async function applyDeepLinkRoute(): Promise<void> {
  try {
    const ctx = await microsoftTeams.app.getContext();
    const sub = ctx.page?.subPageId;
    if (!sub) return;
    // Defensive: only accept in-app paths to avoid open-redirect via a
    // hand-crafted activity notification.
    if (!sub.startsWith("/")) return;
    if (window.location.pathname + window.location.search === sub) return;
    window.history.replaceState({}, "", sub);
  } catch {
    // outside Teams or older host without getContext — ignore
  }
}

interface SsoResponse {
  token: string;
  expiresAt: string;
  user: { id: number; email: string; name: string };
}

// Returns true if SSO succeeded (Bearer token stored), false otherwise.
// Idempotent: a fresh page load will re-run; api.ts can survive without.
export async function tryTeamsSso(): Promise<boolean> {
  if (!probablyInTeams()) return false;
  const initialized = await initTeams();
  if (!initialized) return false;

  // Rewrite the URL *before* the app mounts, so wouter-preact's first render
  // already lands on the deep-linked route instead of /.
  await applyDeepLinkRoute();

  let token: string;
  try {
    token = await microsoftTeams.authentication.getAuthToken();
  } catch (err) {
    console.warn("[teams-sso] getAuthToken failed", err);
    return false;
  }

  try {
    const res = await fetch("/api/auth/teams-sso", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      console.warn("[teams-sso] backend rejected token", res.status);
      return false;
    }
    const data = (await res.json()) as SsoResponse;
    setTeamsBearer(data.token);
    return true;
  } catch (err) {
    console.warn("[teams-sso] exchange failed", err);
    return false;
  }
}
